import React from 'react';
import type { ShippingProviderEnum } from '../../../../generated/graphql';

type ItemShippingTypeEnum = 'delivery' | 'clickAndCollect';

export interface ItemShippingType {
    type: ItemShippingTypeEnum;
    ID: number;
    provider: ShippingProviderEnum | null;
}

interface ItemShippingState {
    variantShipping: ItemShippingType | null;
    setVariantShipping(shipping: ItemShippingType | null): void;
    sampleShipping: ItemShippingType | null;
    setSampleShipping(shipping: ItemShippingType | null): void;
}

export const makeShippingOption = (
    type: ItemShippingTypeEnum,
    ID: number,
    provider?: ShippingProviderEnum | null
): ItemShippingType => {
    return { type, ID, provider: provider ?? null };
};

const ItemShippingContext = React.createContext<ItemShippingState | undefined>(
    undefined
);

export const useItemShipping = () => {
    const context = React.useContext(ItemShippingContext);
    if (!context) {
        throw new Error('Cannot be used outside ItemShippingContext');
    }
    return context;
};

interface ItemShippingProviderProps {
    children: React.ReactNode;
}

export const ItemShippingProvider = ({
    children,
}: ItemShippingProviderProps) => {
    const variantShippingState = React.useState<ItemShippingType | null>(null);
    const sampleShippingState = React.useState<ItemShippingType | null>(null);

    const value = React.useMemo(() => {
        const [variantShipping, setVariantShipping] = variantShippingState;
        const [sampleShipping, setSampleShipping] = sampleShippingState;

        return {
            variantShipping,
            setVariantShipping,
            sampleShipping,
            setSampleShipping,
        };
    }, [variantShippingState, sampleShippingState]);

    return (
        <ItemShippingContext.Provider value={value}>
            {children}
        </ItemShippingContext.Provider>
    );
};
